import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import usePDPPhotos from 'data-hooks/usePDPPhotos/index';
import usePDPCylindoViewer from 'data-hooks/usePDPCylindoViewer';
import useOnEsc from 'hooks/useOnEsc';
import usePDPViewGalleryImageEvent from 'data-hooks/useEvents/usePDPViewGalleryImageEvent';
import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import useRemoveBrokenImageUrls from 'global-state/brokenImageUrls/useRemoveBrokenImageUrls';
import RatioContainer from 'components/consumer/RatioContainer';
import JBProductImage from 'components/essentials/JBImage/JBProductImage';
import Placeholder from 'components/consumer/Placeholder';
import PDPCylindo3dViewer from 'components/consumer/PDP/PDPCylindo3dViewer';
import PDPDecideLaterOverlay from 'components/consumer/PDP/PDPDecideLaterOverlay';
import PDPGalleryLabelsAndViewingCount from 'components/consumer/PDP/PDPGalleryLabelsAndViewingCount';
import PDPFavoriteButton from 'components/consumer/PDPFavoriteButton';
import { markLuxPageNavigationEnd } from 'commons/lux';
import IconPlus from 'components/icons/iconPlus';
import IconMinus from 'components/icons/iconMinus';
import IconClose from 'components/icons/iconClose';
import { getMainImageImgixPropsForPhoto } from '../PDPDesktopHeroGallery/helpers/imgix-props';
import usePDPDesktopHeroSizeProps from '../PDPDesktopHeroGallery/helpers/usePDPDesktopHeroSizeProps';
import PDPDimensionShotViewer from '../PDPDimensionShotViewer';
import PDPProductIntroVideo from '../PDPDesktopHeroGallery/PDPProductIntroVideo';
import PDPGalleryRevampCircleButton from './PDPGalleryRevampCircleButton';
import PDPGalleryRevampControlsBar from './PDPGalleryRevampControlsBar';
import PDPGalleryRevampLifestyleRail from './PDPGalleryRevampLifestyleRail';
import PDPGalleryRevampExpandedGallery from './PDPGalleryRevampExpandedGallery';
import clickViewerControl from './clickViewerControl';
import useCylindoARActive from '../hooks/useCylindoARActive';

// Cap the gallery like the legacy maxWidth cap so image + controls stay in the viewport
const GALLERY_RATIO = 1 / 2;
const VERTICAL_SPACE_AROUND = 32 + 45 + 90;
const RAIL_TOTAL_WIDTH = 72; // 56px rail + 16px gap
const GALLERY_MAX_WIDTH = `calc((100vh - ${VERTICAL_SPACE_AROUND}px) / ${GALLERY_RATIO} + ${RAIL_TOTAL_WIDTH}px)`;
const REVAMP_VIEWER_ID = 'PDPDesktopGalleryRevampViewer';

// Redesigned desktop gallery (CON-8444), behind ?galleryRevamp=1 for now
const PDPDesktopGalleryRevamp = ({
  slug,
  productHeroImage = null,
  productCylindoHeroImage = null
}) => {
  const {
    loading,
    error,
    data,
    actions
  } = usePDPPhotos(null, slug);
  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();
  const {
    viewGalleryImageEvent
  } = usePDPViewGalleryImageEvent();
  const {
    cylindoFeatures,
    isCylindoAvailable,
    cylindoConfigurationPhotos,
    isCylindoConfigurationPhotos,
    isMechanicalOpen,
    toggleMechanicalOpen,
    dimensionShotUrls,
    currentDimensionShot,
    selectedDimensionShotIndex,
    hasMultipleDimensionShots,
    shouldShowDimensionThumbnail,
    goToNextDimensionShot,
    goToPreviousDimensionShot
  } = usePDPCylindoViewer({
    isPDPDesktop: true,
    deferCylindoFetch: true
  });
  const [isMeasureActive, setIsMeasureActive] = useState(false);
  const [expandedGalleryIndex, setExpandedGalleryIndex] = useState(null);
  const {
    isARActive,
    rearm: rearmARWatcher
  } = useCylindoARActive(REVAMP_VIEWER_ID);
  const {
    configurationPhotos: nonCylindoConfigurationPhotos,
    dimensionalImage,
    enableCrops,
    introVideoIds,
    isConfigFlipped,
    isVideoSelected,
    productName,
    selectedPhoto,
    studioPhotos: allStudioPhotos
  } = data ?? {};
  const {
    setSelectedPhoto,
    setIsVideoSelected
  } = actions ?? {};
  const configurationPhotos = useRemoveBrokenImageUrls(isCylindoConfigurationPhotos ? cylindoConfigurationPhotos : nonCylindoConfigurationPhotos);
  const studioPhotos = useRemoveBrokenImageUrls(allStudioPhotos);

  // selectPhoto clears isVideoSelected, so this drops back to the 360 spin
  const closeVideoView = useCallback(() => setSelectedPhoto?.(configurationPhotos[0] ?? null), [configurationPhotos, setSelectedPhoto]);
  const closeMeasureView = useCallback(() => setIsMeasureActive(false), []);
  const escapeTarget = isMeasureActive ? closeMeasureView : closeVideoView;
  useOnEsc((isMeasureActive || !!isVideoSelected) && expandedGalleryIndex == null, escapeTarget);
  const mainPhotoSizeProps = usePDPDesktopHeroSizeProps();
  const showDecideLaterOverlay = pdpSelectedCustomizerTab === 'decide-later';

  // Falls back to the static dimensional image when there are no Cylindo shots
  const currentDimensionShotToShow = useMemo(() => {
    if (currentDimensionShot) {
      return currentDimensionShot;
    }
    if (dimensionalImage?.src) {
      return {
        src: dimensionalImage.src,
        dimensionCode: 'static',
        id: 'static-dimensional'
      };
    }
    return null;
  }, [currentDimensionShot, dimensionalImage]);
  if (loading) {
    // Prefer the server-resolved hero image over a placeholder while
    // usePDPPhotos loads, same as the legacy gallery (CON-8281 LCP).
    const ssrFallbackSrc = productCylindoHeroImage?.src ? productCylindoHeroImage.src : productHeroImage?.src;
    const ssrFallbackPhoto = ssrFallbackSrc ? {
      type: 'studio',
      src: ssrFallbackSrc
    } : null;
    return <div className="mb-8 ml-auto mr-auto relative" style={{
      maxWidth: GALLERY_MAX_WIDTH
    }} data-xc="PDPDesktopGalleryRevamp">
        <RatioContainer ratio={GALLERY_RATIO}>
          {ssrFallbackPhoto?.src ? <JBProductImage className="h-full w-full object-contain object-center select-none" {...getMainImageImgixPropsForPhoto(ssrFallbackPhoto)} lazy={false} priority /> : <Placeholder type="rect" loading />}
        </RatioContainer>
      </div>;
  }
  if (!data) {
    return null;
  }
  if (error) {
    return 'Something went wrong, please try again';
  }
  const isExpandedGalleryOpen = expandedGalleryIndex != null;
  const introVideoId = introVideoIds?.desktop;
  const showMeasureView = isMeasureActive && !!currentDimensionShotToShow;
  const showVideoView = !showMeasureView && isVideoSelected && !!introVideoId;
  const showCylindoViewer = isCylindoAvailable && isCylindoConfigurationPhotos && !showMeasureView && !showVideoView;
  const fallbackPhoto = selectedPhoto?.src && selectedPhoto.type !== 'dimensional' ? selectedPhoto : configurationPhotos[0] ?? studioPhotos[0];
  const selectedConfigIndex = Math.max(0, configurationPhotos.findIndex(({
    src
  }) => src === selectedPhoto?.src));
  const isConfigPhotoSelected = selectedPhoto?.type === 'configuration' || selectedPhoto?.type === 'cylindo';
  return <div className="mb-8 ml-auto mr-auto relative" style={{
    maxWidth: GALLERY_MAX_WIDTH
  }} data-xc="PDPDesktopGalleryRevamp">
      <div className="flex gap-4">
        <PDPGalleryRevampLifestyleRail introVideoId={introVideoId} isVideoSelected={showVideoView} onExpand={setExpandedGalleryIndex} onSelectVideo={() => setIsVideoSelected?.()} photos={studioPhotos} viewGalleryImageEvent={viewGalleryImageEvent} />

        <div className="flex-1 min-w-0">
          <RatioContainer className={classnames('bg-transparent border-0 focus:outline-0',
        // Cylindo's AR panel ships a slate-blue backdrop; restyle it to Joybird
        // teal and keep its close button visible while our fabs stay hidden
        '[&_cylindo-viewer::part(fab)]:hidden [&_cylindo-viewer::part(qr-fab)]:!flex [&_cylindo-viewer::part(qr-backdrop)]:!bg-[rgba(16,124,140,0.8)] [&_cylindo-viewer::part(qr-dialog)]:border-2 [&_cylindo-viewer::part(qr-dialog)]:border-solid [&_cylindo-viewer::part(qr-dialog)]:border-brand')} ratio={GALLERY_RATIO}>
            {showMeasureView && <PDPDimensionShotViewer currentDimensionShot={currentDimensionShotToShow} hasMultipleDimensionShots={hasMultipleDimensionShots} selectedDimensionShotIndex={selectedDimensionShotIndex} totalDimensionShots={dimensionShotUrls.length} onPrevious={goToPreviousDimensionShot} onNext={goToNextDimensionShot} arrowButtonClass="w-9 h-9 bg-white border border-solid border-gray-light6 hover:bg-gray-light9 [&_svg_path]:fill-gray-dark" className="h-full w-full" lazy={false} onLoad={markLuxPageNavigationEnd} />}

            {showVideoView && <PDPProductIntroVideo videoId={introVideoId} />}

            {!showMeasureView && !showVideoView && !showCylindoViewer && !!fallbackPhoto?.src && <JBProductImage className={classnames('h-full w-full object-contain object-center select-none', {
            'scale-x-[-1]': fallbackPhoto.type !== 'studio' && isConfigFlipped
          })} {...getMainImageImgixPropsForPhoto(fallbackPhoto, enableCrops)} {...mainPhotoSizeProps} lazy={false} priority onLoad={markLuxPageNavigationEnd} />}

            {isCylindoAvailable && <div className={classnames('w-full h-full [&_.mxt-btn-download]:hidden [&_.mxt-btn-expand]:hidden',
          // Product asked for 15-20% off the desktop render, which read as
          // oversized for the gallery box; 0.85 is the low end (CON-8444).
          // Desktop only - mobile is full-bleed and doesn't need it.
          '[&_cylindo-viewer]:scale-[0.85] [&_cylindo-viewer]:origin-center', {
            invisible: !showCylindoViewer
          })}>
                <PDPCylindo3dViewer containerId={REVAMP_VIEWER_ID} cylindoFeatures={cylindoFeatures} rotateIndex={isConfigPhotoSelected ? selectedConfigIndex : -1} />
              </div>}

            {showDecideLaterOverlay && <PDPDecideLaterOverlay />}
          </RatioContainer>
        </div>
      </div>

      <PDPGalleryRevampControlsBar isMeasureActive={showMeasureView}
    // Only one of measure / open can be active, so turning measure on
    // closes the mechanical view the same way opening clears measure
    onToggleMeasure={() => {
      if (!showMeasureView && isMechanicalOpen) {
        toggleMechanicalOpen();
      }
      setIsMeasureActive(!showMeasureView);
    }} showMeasure={shouldShowDimensionThumbnail} isMechanicalOpen={isMechanicalOpen} toggleMechanicalOpen={() => {
      // Last interaction wins: toggling open/close leaves measure view
      setIsMeasureActive(false);
      toggleMechanicalOpen();
    }} onLaunchViewerAR={isCylindoAvailable && isCylindoConfigurationPhotos ? () => {
      setIsMeasureActive(false);
      clickViewerControl(REVAMP_VIEWER_ID, 'view-in-ar');
      rearmARWatcher();
    } : null} />

      {/* Overlays the hero + controls area; viewer stays mounted underneath */}
      {isExpandedGalleryOpen && <div className="absolute inset-0 z-[3] bg-gray-light8">
          <PDPGalleryRevampExpandedGallery enableCrops={enableCrops} initialIndex={expandedGalleryIndex} introVideoId={introVideoId} onClose={() => setExpandedGalleryIndex(null)} photos={studioPhotos} productName={productName} viewGalleryImageEvent={viewGalleryImageEvent} />
        </div>}

      {/* Hidden during AR so it can't cover Cylindo's close button (CON-8444) */}
      <div className={classnames('flex justify-between left-20 absolute right-4 top-4 z-[2]', {
      hidden: isARActive
    })}>
        <PDPGalleryLabelsAndViewingCount mode="pdp" />
        <div className="flex flex-col gap-2 items-end">
          <PDPFavoriteButton />
          {showMeasureView && <PDPGalleryRevampCircleButton ariaLabel="Close measurements" onClick={() => setIsMeasureActive(false)}>
              <IconClose width={9} height={9} />
            </PDPGalleryRevampCircleButton>}
          {showVideoView && <PDPGalleryRevampCircleButton ariaLabel="Close video" onClick={closeVideoView}>
              <IconClose width={9} height={9} />
            </PDPGalleryRevampCircleButton>}
          {showCylindoViewer && <>
              <PDPGalleryRevampCircleButton ariaLabel="Zoom in" onClick={() => clickViewerControl(REVAMP_VIEWER_ID, 'zoom-in')}>
                <IconPlus width={15} height={15} />
              </PDPGalleryRevampCircleButton>
              <PDPGalleryRevampCircleButton ariaLabel="Zoom out" onClick={() => clickViewerControl(REVAMP_VIEWER_ID, 'zoom-out')}>
                <IconMinus width={15} height={15} />
              </PDPGalleryRevampCircleButton>
            </>}
        </div>
      </div>
    </div>;
};
PDPDesktopGalleryRevamp.propTypes = {
  slug: PropTypes.string,
  productHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  }),
  productCylindoHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  })
};
export default PDPDesktopGalleryRevamp;